import { computed, DestroyRef, Directive, inject, OnInit, Signal } from '@angular/core';
import { DisplayAssetDependenciesListComponent } from '@r1-compass-library/components/display-asset-dependencies-list/display-asset-dependencies-list.component';
import {
    AssetStateMessageHandlingService,
    AssetListSortingService,
    SnackBarService,
} from '@r1-compass-library/services';
import { filterNil } from 'ngxtension/filter-nil';
import { AppConstants } from './app.constants';
import { MatDialog } from '@angular/material/dialog';
import { AssetListStore } from '@r1-compass-library/store/asset-list.store';
import { FolderRestService } from '@r1-compass-library/services/rest/folder-rest.service';
import { AssetRestService } from '@r1-compass-library/services/rest/asset-rest.service';
import { AssetMetadata, BasicFolder, DivisionConfig, isAsset, isFolder } from '@r1-compass-library/models';
import { AssetSearch } from '@r1-compass-library/models/search/asset-search.model';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import {
    buildFolderBreadcrumbs,
    EditFolderDialogComponent,
    EditFolderDialogData,
    FileTableItem,
    GenericConfirmationDialogComponent,
    GenericConfirmationDialogData,
    TableColumn,
} from '@r1-compass-library/components';
import {
    MoveFileComponent,
    MoveFileDialogData,
} from '@r1-compass-library/components/dialog/move-file/move-file.component';
import { BasicFolderSearch } from '@r1-compass-library/models/search/basic-folder-search.model';
import { BehaviorSubject, combineLatest, exhaustMap, forkJoin, from, iif, map, Observable, of, switchMap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FileTableEntry } from '@r1-compass-library/components/file-table/file-table.model';
import { Dependency } from '@r1-compass-library/models/dependencies.model';
import { PlayValidationStateService } from './plays/play-validation-state.service';
import { ContentLibraryValidationStateService } from './content-library/content-library-validation-state.service';
import { LifecycleRulesValidationStateService } from './lifecycle-rules/lifecycle-rules-validation-state.service';
import { TemplateCriteriaValidationStateService } from './template-criteria/template-criteria-validation-state.service';

@Directive()
export abstract class AssetListComponent<
    A extends AssetMetadata,
    ARS extends AssetRestService<A, AssetSearch>,
    FRS extends FolderRestService<BasicFolder, BasicFolderSearch>,
    ALS extends AssetListStore<A, ARS, FRS>,
> implements OnInit
{
    readonly #dialog = inject(MatDialog);
    readonly #globalLoggedInUserStore: GlobalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #destroyRef = inject(DestroyRef);
    readonly #snackBarService = inject(SnackBarService);

    // Used mainly by plays-list.component.ts
    protected readonly router: Router = inject(Router);
    protected readonly route: ActivatedRoute = inject(ActivatedRoute);
    protected readonly assetStateMessageHandlingService = inject(AssetStateMessageHandlingService);
    protected readonly assetListSortingService = inject(AssetListSortingService);

    abstract EDIT_ASSET_PATH: string;
    abstract BASE_PATH: string;

    folderId = AppConstants.defaultRootFolderId;
    columns: TableColumn[] = AppConstants.folderNavColumns;

    breadcrumbs = computed(() => {
        return buildFolderBreadcrumbs(this.vm().currentFolder, this.BASE_PATH, false);
    });

    divisionConfig: Signal<DivisionConfig | undefined> = this.#globalLoggedInUserStore.selectedDivisionConfig;
    loggedInUserId: Signal<number | undefined> = this.#globalLoggedInUserStore.selectedUserId;
    private divisionId = computed(() => this.divisionConfig()?.iamDivisionId);
    private search$ = new BehaviorSubject('');

    private searchContent$: Observable<FileTableItem[]> = this.search$.pipe(
        switchMap(x =>
            iif(
                () => x.length >= 2,
                of(x).pipe(
                    switchMap(search =>
                        forkJoin([
                            this.folderRestService.search({
                                iamDivisionId: this.divisionId(),
                                advancedSearch: [{ property: 'name', value: search, operator: 'ilike' }],
                            }),
                            this.assetRestService.search({
                                iamDivisionId: this.divisionId(),
                                advancedSearch: [{ property: 'name', value: search, operator: 'ilike' }],
                            }),
                        ])
                    ),
                    map(res => {
                        const content: FileTableItem[] = [];
                        res.forEach(item => item.forEach(assetOrFolder => content.push(assetOrFolder)));
                        return content;
                    })
                ),
                of([])
            )
        )
    );
    public searchContent = toSignal(this.searchContent$, { initialValue: [] });

    vm: Signal<{
        assets: A[];
        folders: BasicFolder[];
        currentFolder: BasicFolder | null;
        contentWithActions: FileTableEntry[];
        dependencies: Dependency[] | undefined;
    }>;

    protected sortedFileTableEntries = computed(() =>
        this.assetListSortingService.defaultSort(this.vm().contentWithActions)
    );

    constructor(
        // eslint-disable-next-line @angular-eslint/prefer-inject
        protected assetListStore: ALS,
        // eslint-disable-next-line @angular-eslint/prefer-inject
        protected assetRestService: ARS,
        // eslint-disable-next-line @angular-eslint/prefer-inject
        protected folderRestService: FRS
    ) {
        this.vm = this.assetListStore.vm;

        this.resetValidityOfEntitySections();
    }

    ngOnInit(): void {
        this.assetListStore.reset();
        combineLatest([
            this.route.queryParams,
            this.assetListStore.usersAvailableDivisions$,
            this.assetListStore.selectedIamDivision$.pipe(filterNil()),
        ])
            .pipe(
                map(([queryParams, usersAvailableDivisions, selectedIamDivision]) => ({
                    queryParams,
                    usersAvailableDivisions,
                    selectedIamDivision,
                })),
                switchMap(res => {
                    this.folderId = Number(res.queryParams['folderId'] as string);

                    if (this.folderId === 0) {
                        return from(
                            this.assetListStore.getAssetsAndFoldersInRoot({
                                rootFolder: true,
                                divisionId: res.selectedIamDivision?.id,
                            })
                        );
                    } else {
                        //we grab this here to ensure we have the correct folderId to use in the later assetListStore.getAssetsAndFolders
                        //to prevent a race condition in case this value changed partway through this process
                        const folderId = this.folderId;
                        return from(
                            this.assetListStore.getCurrentFolder({
                                ...res,
                                folderId: folderId,
                            })
                        ).pipe(
                            exhaustMap(() =>
                                from(
                                    this.assetListStore.getAssetsAndFolders({
                                        ...res,
                                        folderId: folderId,
                                    })
                                )
                            )
                        );
                    }
                }),
                takeUntilDestroyed(this.#destroyRef)
            )
            .subscribe();

        this.assetListStore.message$?.pipe(filterNil(), takeUntilDestroyed(this.#destroyRef)).subscribe(message => {
            this.assetStateMessageHandlingService.handleMessage(message);
            this.assetListStore.resetMessage();
        });

        this.assetListStore.copiedAsset$?.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(asset => {
            const assetName = asset?.name || 'Asset';
            this.#snackBarService.openGenericSnackbar({
                messages: [`${assetName} copied successfully.`],
                duration: 5000,
                showDismissButton: true,
                action: () => this.onEditAsset(asset),
            });
        });
        this.assetListStore.movedAsset$?.pipe(filterNil(), takeUntilDestroyed(this.#destroyRef)).subscribe(asset => {
            const assetName = asset?.name || 'Asset';
            this.#snackBarService.openGenericSnackbar({
                messages: [`${assetName} moved successfully.`],
                duration: 5000,
                showDismissButton: true,
                action: () => this.onEditAsset(asset),
            });
        });
        this.assetListStore.movedFolder$?.pipe(filterNil(), takeUntilDestroyed(this.#destroyRef)).subscribe(folder => {
            const folderName = folder?.name || 'Folder';
            this.#snackBarService.openGenericSnackbar({
                messages: [`${folderName} moved successfully.`],
                duration: 5000,
                showDismissButton: true,
                action: () => this.onMoveFolder(folder),
            });
        });
    }

    goToRoot() {
        this.router.navigate([], { relativeTo: this.route, queryParams: { folderId: 0 } });
    }

    onChangeFolder(folder: BasicFolder) {
        this.router.navigate([], { relativeTo: this.route, queryParams: { folderId: folder.id } });
    }

    onOptionClicked(event: { optionValue: string; item: FileTableItem }) {
        if (event.optionValue === 'edit') {
            if (isFolder(event.item)) {
                this.editFolder(event.item);
            }
        } else if (event.optionValue === 'move') {
            this.move(event.item);
        } else if (event.optionValue === 'copy') {
            this.assetListStore.copyAsset(event.item);
        } else if (event.optionValue === 'delete') {
            this.delete(event.item);
        }
    }

    onCreateAsset() {
        this.router.navigate(['new/definition'], {
            relativeTo: this.route,
            queryParams: { folderId: this.folderId },
        });
    }

    onEditAsset(asset: AssetMetadata) {
        this.router.navigate([asset.id + '/' + `${this.EDIT_ASSET_PATH}`], {
            relativeTo: this.route,
        });
    }

    onCreateFolder() {
        const dialogRef = this.#dialog.open<EditFolderDialogComponent, EditFolderDialogData, string>(
            EditFolderDialogComponent,
            {
                data: { folderName: '' },
                height: '250px',
                width: '400px',
            }
        );

        dialogRef.afterClosed().subscribe(name => {
            if (name) {
                this.assetListStore.saveAssetFolder({
                    name: name,
                    iamDivisionId: this.divisionId(),
                    path: this.BASE_PATH + '?folderId=${id}',
                    parentFolderId: this.folderId,
                    createdBy: this.loggedInUserId(),
                    updatedBy: this.loggedInUserId(),
                });
            }
        });
    }

    onMoveFolder(folder: BasicFolder) {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { folderId: folder.parentFolderId },
        });
    }

    onOpenDependencies(asset: AssetMetadata) {
        this.displayDependencyMessage(asset);
    }

    editFolder(folder: BasicFolder) {
        const dialogRef = this.#dialog.open<EditFolderDialogComponent, EditFolderDialogData, string>(
            EditFolderDialogComponent,
            {
                data: { folderName: folder.name },
                height: '250px',
                width: '400px',
            }
        );
        dialogRef.afterClosed().subscribe(name => {
            if (name) {
                this.assetListStore.saveAssetFolder({
                    id: folder.id,
                    name: name,
                    iamDivisionId: this.divisionId(),
                    path: this.BASE_PATH + `?folderId=${folder.id}`,
                    parentFolderId: this.folderId,
                    updatedBy: this.loggedInUserId(),
                });
            }
        });
    }

    move(item: FileTableItem) {
        const iamDivisionId = this.divisionId();
        if (iamDivisionId) {
            const dialogRef = this.#dialog.open<MoveFileComponent, MoveFileDialogData, number | null>(
                MoveFileComponent,
                {
                    data: {
                        folderService: this.folderRestService,
                        iamDivisionId: iamDivisionId,
                        item: item,
                    },
                    width: '400px',
                }
            );
            dialogRef.afterClosed().subscribe(destinationFolderId => {
                if (destinationFolderId === undefined) {
                    return;
                }
                item = {
                    ...item,
                    updatedBy: this.loggedInUserId()!,
                };
                if (isFolder(item)) {
                    item.parentFolderId = destinationFolderId as number;
                    this.assetListStore.moveFolder({ folder: item, destinationFolderId: destinationFolderId! });
                } else if (isAsset(item)) {
                    this.assetListStore.moveAsset({ asset: item as A, destinationFolderId: destinationFolderId });
                }
            });
        }
    }

    delete(item: FileTableItem) {
        if (isFolder(item)) {
            return;
            //not deleting yet
        }

        if ('dependencyCount' in item) {
            if (item.dependencyCount) {
                this.displayDependencyMessage(item);
            } else {
                this.displayDeleteConfirmationMessage(item);
            }
            //still here or there's no dependencies, delete
        } else {
            this.displayDeleteConfirmationMessage(item);
        }
    }

    protected displayDeleteConfirmationMessage(item: FileTableItem) {
        const dialogRef = this.#dialog.open<GenericConfirmationDialogComponent, GenericConfirmationDialogData>(
            GenericConfirmationDialogComponent,
            {
                width: '400px',
                disableClose: true,
                data: {
                    confirmButton: {
                        color: 'primary',
                        type: 'stroked',
                    },
                    cancelButton: {
                        color: 'black',
                        type: 'outlined',
                    },
                    messageContainsHtml: false,
                    message: 'Are you sure you want to delete?',
                },
            }
        );
        dialogRef.afterClosed().subscribe(confirmation => {
            if (confirmation) {
                this.doDelete(item.id);
            }
        });
    }

    protected displayDependencyMessage(item: FileTableItem) {
        const dependencies = this.assetListStore
            .dependenciesForPlays()
            .filter(deps => deps.at(0)?.assetId === item.id)
            .at(0)
            ?.sort(function (a, b) {
                return a.name.localeCompare(b.name);
            });
        this.#dialog.open(DisplayAssetDependenciesListComponent, {
            width: '600px',
            disableClose: true,
            data: {
                dependencies: dependencies,
                dialogText: `Viewing dependencies for ID: ${item.id}`,
            },
            autoFocus: false,
        });
    }

    private doDelete(id: number) {
        this.assetListStore.deleteAsset(id);
    }

    public onTypeaheadSearch(searchString: string) {
        this.search$.next(searchString);
    }

    private resetValidityOfEntitySections() {
        inject(PlayValidationStateService).resetValidityOfSections();
        inject(ContentLibraryValidationStateService).resetValidityOfSections();
        inject(LifecycleRulesValidationStateService).resetValidityOfSections();
        inject(TemplateCriteriaValidationStateService).resetValidityOfSections();
    }
}
